import type { CloudVault } from '../vault/cloudVault';

/**
 * Filename → path, over the whole vault.
 *
 * Membership refs name a FILE (`Recording 2026-08-01 at 10-51.md`), not a
 * path, so resolving one means knowing where that name lives. Reading the
 * files to find out is not an option at this scale — the vault holds
 * thousands — but the manifest already carries every path, so the index is
 * string work over data we hold anyway.
 *
 * Ambiguity is real: two folders can hold the same filename. The first by
 * sorted path wins and the collision is remembered, so a surface can say
 * "this ref is ambiguous" rather than silently opening the wrong file.
 */
export interface VaultIndex {
  pathFor(name: string): string | null;
  isAmbiguous(name: string): boolean;
  size: number;
}

export function buildVaultIndex(vault: CloudVault): VaultIndex {
  const byName = new Map<string, string>();
  const collisions = new Set<string>();

  for (const path of vault.listPaths('')) {
    const name = path.replace(/^.*\//, '');
    if (byName.has(name)) collisions.add(name);
    else byName.set(name, path);
  }

  return {
    pathFor: (name) => byName.get(name) ?? null,
    isAmbiguous: (name) => collisions.has(name),
    size: byName.size,
  };
}
